import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { EfcBaseHttpService } from './efc-base-http.service';
import { map, switchMap } from 'rxjs/operators';
import { countryCd, dealerCd, environment } from './util.service';
import { SeriesData } from '../models/series-details';
import { VehicleImage } from '../models/vehicle-image';
import { DcsBaseHttpService } from './dcs-base-http.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { IResponse, ISSCPayload, ITelematics } from '@signal/asp-data-commons';
import { selectDealerCode } from '../../store/dealer/dealer.selectors';
import { Store } from '@ngrx/store';
import { DealerState } from '../../store/dealer/dealer.reducer';
import { IToyotaLexusModelDetails } from '../../store/service-appointment/service-appointment.reducer';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class VehicleService {
  efcApi: string;
  dcsb2oApi: string;
  aspAppointmentApi: string;
  aspCatalogApi: string;
  makeModelYear: IToyotaLexusModelDetails;
  vinList: string[] = [];
  telematicsDetailsForVin: { vin: string, telematicsData: any }[] = [];
  selectedCustomerTelematicsDetails = [];
  httpOptions = {
    headers: new HttpHeaders()
  };
  constructor(private readonly efcBaseHttpService: EfcBaseHttpService,
    private readonly dcsBaseService: DcsBaseHttpService,
    private readonly http: HttpClient,
    private readonly dealerState: Store<DealerState>,private translate: TranslateService) {
    this.efcApi = environment.efcApi;
    this.dcsb2oApi = environment.dcsb2oApi;
    this.aspAppointmentApi = environment.aspAppointmentApi;
    this.aspCatalogApi = environment.aspCatalogApi;
  }

  getSeriesData(brand: string): Observable<SeriesData> {
    const url = this.efcApi + `${brand}/NATIONAL/EN/series`;
    return this.efcBaseHttpService.get(url).pipe(
      map(
        (data: any) => {
          return data;
        }
      )
    );
  }

  getVehicleColors(brand: string, year: string, series: string, trim: string): Observable<VehicleImage> {
    const seriesLowerCase = series.replace(/\W+/g, '').toLowerCase()
    const url = this.efcApi + `${brand}/NATIONAL/EN/colors?year=${year}&series=${seriesLowerCase}&trim=${trim}`;
    return this.efcBaseHttpService.get(url).pipe(
      map(
        (data: any) => {
          return data;
        }
      )
    );
  }

  getDcsSeriesData(): Observable<IToyotaLexusModelDetails> {
    if (this.makeModelYear !== undefined) {
      return new Observable((observer) => {
        observer.next(this.makeModelYear);
        observer.complete();
      });
    }
    else {
      let url =  `${(this.aspCatalogApi)}catalog/${dealerCd}/${countryCd}/model-year`;
      if(countryCd.includes('US')) {
        url =  `${(this.aspCatalogApi)}catalog/${dealerCd}/us/model-year`;
      }
      const httpToyotaUrl = this.dcsBaseService.get(url, '1.0', { xBrand: 'T', xClient: 'TO-WEB' });
      const httpLexusUrl = this.dcsBaseService.get(url, '1.0', { xBrand: 'L', xClient: 'TO-WEB' });
    

      return httpToyotaUrl.pipe(
        switchMap((toyotaData: SeriesData) => {
          return httpLexusUrl.pipe(
            map((lexusData: SeriesData) => {
              if (environment.useEOSForDCS) {
                this.makeModelYear = {
                  toyota: toyotaData.data.vehicleModelYearList,
                  lexus: lexusData.data.vehicleModelYearList
                };
                return {
                  toyota: toyotaData.data.vehicleModelYearList,
                  lexus: lexusData.data.vehicleModelYearList
                };
              }
              this.makeModelYear = {
                toyota: toyotaData.data.vehicleModelYearList,
                lexus: lexusData.data.vehicleModelYearList
              }
              return {
                toyota: toyotaData.vehicleModelYearList,
                lexus: lexusData.vehicleModelYearList
              };
            }));
        }
        )
      );
    }
  }

  getCountryAndLang(){
    const countryCd = this.translate.currentLang? this.translate.currentLang.split('-')[1]: "US";
    const languageCd = this.translate.currentLang? this.translate.currentLang.split('-')[0]: "en";
    return {countryCd,languageCd}
  }

  getTelematics(vin: string,department:string): Observable<IResponse<ITelematics>> {
    const vinIndex = this.vinList.indexOf(vin);
    const isVinCached = vinIndex !== -1 && this.telematicsDetailsForVin[vinIndex].vin === vin;
  
    if (isVinCached) {
      return this.getCachedTelematics(vin, vinIndex);
    } else {
      return this.fetchTelematicsFromServer(vin,department);
    }
  }
  
  private getCachedTelematics(vin: string, vinIndex: number): Observable<IResponse<ITelematics>> {
    return new Observable((observer) => {
      const telematicsData = this.selectedCustomerTelematicsDetails.find(tele => tele?.vin === vin);
      if (!telematicsData) {
        this.selectedCustomerTelematicsDetails.push(this.telematicsDetailsForVin[vinIndex]);
      }
      observer.next(this.telematicsDetailsForVin[vinIndex].telematicsData);
      observer.complete();
    });
  }
  
  private fetchTelematicsFromServer(vin: string,department:string): Observable<IResponse<ITelematics>> {
    return this.dealerState.select(selectDealerCode).pipe(
      switchMap(dealerCode => {
        const { countryCd, languageCd } = this.getCountryAndLang();
        const url: string = `${this.aspAppointmentApi}vehicle/${dealerCode}/${vin}/${countryCd}/${languageCd}/${department}/telematics`;
        return this.http.get(url).pipe(
          map((data: any) => {
            this.cacheTelematicsData(vin, data);
            return data;
          })
        );
      })
    );
  }
  
  fetchSSCdetailsFromServer(vin: string): Observable<IResponse<ISSCPayload>> {
    return this.dealerState.select(selectDealerCode).pipe(
      switchMap(dealerCode => {
        const { countryCd, languageCd } = this.getCountryAndLang();
        const url: string = `${this.aspAppointmentApi}vehicle/${dealerCode}/${vin}/${countryCd}/${languageCd}/ssc`;
        return this.http.get(url).pipe(
          map((data: any) => {
            return data;
          })
        );
      })
    );
  }

  private cacheTelematicsData(vin: string, data: any) {
    this.vinList.push(vin);
    const telematicsData = this.selectedCustomerTelematicsDetails.find(tele => tele?.vin === vin);
    if (!telematicsData) {
      this.selectedCustomerTelematicsDetails.push({ vin, telematicsData: data });
    }
    this.telematicsDetailsForVin.push({ vin, telematicsData: data });
  }

  getVehicleFromVin(vin, source=""){
    if(vin.length === 17){
      return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode=>{
        const url = `${this.aspAppointmentApi}vehicle/${dealerCode}/vehicledata/testdrive`;
        const data = {vin:vin};
        if(source) {
          data['source'] = source;
        }
        return this.http.post(url, data).pipe(
          map((data: any) => {
            return data;
          }
          )
        );
      }));
    }
    return new Observable(obs=>{
      obs.next({data:[]});
      obs.complete();
    });
  }

  getVehicleColorForLexus(value, isTestDriveVehicle: boolean) {
    const opCodeRequest = value.vin && value.vin.length === 17 ? { "vin": value.vin } : (value.make ? { "mileage": value.mileage, "model": value.model, "year": value.year, "make": value.make } : null)
      if (opCodeRequest) {
       return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode=>{
        const url = `${this.aspAppointmentApi}vehicle/${dealerCode}/vehicledetails/${isTestDriveVehicle}`;
        return this.http.post(url, opCodeRequest).pipe(
          map((data: any) => {
            return data;
          }
          )
        );
      }));
      }
      return new Observable(obs=>{
        obs.next({data:[]});
        obs.complete();
      });
  }
  clearCurrentCustomerTelematics() {
    this.selectedCustomerTelematicsDetails = [];
  }

  checkAppointmentsForVin(vin){
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode=>{
      const url = `${this.aspAppointmentApi}appointment/${dealerCode}/${vin}`;
      return this.http.get(url).pipe(
        map((data: any) => {
          return data;
        }
        )
      );
    }));
  }

  getVehicleDetails(data, isDigiChecklist = false) {
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode => {
      const url = `${this.aspAppointmentApi}vehicle/${dealerCode}/vehicledata?isDigiChecklist=${isDigiChecklist}`;
      return this.http.post(url, data).pipe(
        map((data: any) => {
          return data;
        }
        )
      );
    }));
  }

  searchByLicensePlate(licensePlate, state){
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode => {
      const url = `${this.aspAppointmentApi}vehicle/${dealerCode}/licensePlatedetails?licensePlate=${licensePlate}&stateCode=${state}`;
      return this.http.get(url).pipe(
        map((data: any) => {
          return data;
        }
        )
      );
    }));
  }
}